import type { TFunction } from "i18next";
import { z } from "zod";

// DFT-0081: mirrors bay-hire-service's UploadReceiptCommand.receiptAmount's @DecimalMax,
// itself derived from receipt_amount's NUMERIC(12,2) column capacity (10 integer digits, 2
// decimal places) - same value as preApproveDialogSchema.ts's MAX_TOTAL_AMOUNT. No shared
// constant across the two repos (see DFT-0081 plan.md Decision 7) - kept in sync by comment.
const MAX_RECEIPT_AMOUNT = 9999999999.99;

// DFT-0081: Receipt Number stays free text (confirmed by requester - NOT converted to a
// numeric-only field) - only its length is capped, matching Total Amount's own digit-count
// limit for cross-field consistency, mirroring UploadReceiptCommand.receiptNumber's
// @Size(max = 10).
const MAX_RECEIPT_NUMBER_LENGTH = 10;

export const RECEIPT_UPLOAD_DIALOG_FIELDS = {
  RECEIPT_NUMBER: "receiptNumber",
  RECEIPT_DATE: "receiptDate",
  RECEIPT_AMOUNT: "receiptAmount",
} as const;

export const makeReceiptUploadDialogSchema = (t: TFunction) =>
  z.object({
    receiptNumber: z
      .string()
      .min(1, "Receipt number is required")
      .max(MAX_RECEIPT_NUMBER_LENGTH, t("bay-hire.receipt-upload.number-too-long")),
    receiptDate: z.string().min(1, "Receipt date is required"),
    receiptAmount: z
      .string()
      .min(1, "Receipt amount is required")
      .refine((v) => !Number.isNaN(Number(v)) && Number(v) > 0, {
        message: "Receipt amount must be positive",
      })
      .refine((v) => Number(v) <= MAX_RECEIPT_AMOUNT, {
        message: t("bay-hire.receipt-upload.amount-exceeds-max", { max: MAX_RECEIPT_AMOUNT }),
      }),
  });

/**
 * Retained for callers/tests that do not need a real `t`. DFT-0081: the identity function is
 * only ever used for this export's `z.infer` type shape below - it never actually runs, so a
 * real i18n instance isn't needed here.
 */
export const receiptUploadDialogSchema = makeReceiptUploadDialogSchema(
  ((key: string) => key) as TFunction,
);

export type ReceiptUploadDialogFormData = z.infer<typeof receiptUploadDialogSchema>;

export const receiptUploadDialogDefaultValues: ReceiptUploadDialogFormData = {
  receiptNumber: "",
  receiptDate: "",
  receiptAmount: "",
};
